import type { IconName } from '../components/icons';
import type { Messages } from '../i18n/fr';
import type { PlanFeatureKey } from '../lib/api';

/**
 * The app's information architecture (refonte): one model drives the desktop
 * sidebar, the phone tab bar and its More sheet, the ⌘K palette and the
 * Settings sub-nav, so the four can never disagree about where a page lives.
 */

/** A `nav.*` catalogue key: the label in the current language (redesign Phase 3). */
export type NavLabel = Exclude<keyof Messages['nav'], `${string}_one` | `${string}_other`>;

export interface NavEntry {
  to: string;
  label: NavLabel;
  icon: IconName;
  /** Active only on exactly this path (the dashboard). */
  end?: boolean;
  /** Paths under this entry's prefix that belong to another entry. */
  exclude?: string;
  /** A page that belongs to this entry without sharing its prefix. */
  also?: string;
  /** Other words the ⌘K palette finds it by ("story" is not in "Stories"). */
  keywords?: string[];
  /** The plan feature the page sells (D-183): while it is off, the entry carries the plan's name. */
  feature?: PlanFeatureKey;
}

export interface NavGroup {
  label?: NavLabel;
  items: NavEntry[];
}

const DASHBOARD: NavEntry = { to: '/', label: 'dashboard', icon: 'home', end: true };
/** "Conversations" now (glossary); the palette still finds it by its old name. */
const INBOX: NavEntry = { to: '/inbox', label: 'inbox', icon: 'inbox', keywords: ['inbox'] };
const CONTACTS: NavEntry = { to: '/contacts', label: 'contacts', icon: 'users' };
/** Templates are a way to start an automation, opened from it (its list, New automation): no menu entry of their own. */
const AUTOMATIONS: NavEntry = { to: '/automations', label: 'automations', icon: 'workflow', also: '/templates' };
const TEMPLATES: NavEntry = { to: '/templates', label: 'templates', icon: 'template' };
const POSTS: NavEntry = { to: '/posts', label: 'posts', icon: 'calendar', feature: 'scheduledPosts' };
/** Stories are sold as a feature of their own, not a fourth post format (D-137). */
const STORIES: NavEntry = { to: '/stories', label: 'stories', icon: 'story', keywords: ['story'], feature: 'scheduledPosts' };
const CAMPAIGNS: NavEntry = { to: '/campaigns', label: 'campaigns', icon: 'megaphone', feature: 'campaigns' };
const ANALYTICS: NavEntry = { to: '/analytics', label: 'analytics', icon: 'chart' };
const CHANNELS: NavEntry = { to: '/settings/channels', label: 'channels', icon: 'share' };
/** One entry for every settings page; Channels has its own place in the footer. */
const SETTINGS: NavEntry = { to: '/settings', label: 'settings', icon: 'settings', exclude: '/settings/channels' };

export const NAV_GROUPS: NavGroup[] = [
  { items: [DASHBOARD, INBOX] },
  { label: 'groupAudience', items: [CONTACTS] },
  { label: 'groupAutomate', items: [AUTOMATIONS] },
  { label: 'groupPublish', items: [POSTS, STORIES, CAMPAIGNS] },
  { label: 'groupInsights', items: [ANALYTICS] },
];

export const NAV_FOOTER: NavEntry[] = [CHANNELS, SETTINGS];

/** Settings sub-nav. Mon offre has its own icon — no longer Analytics'. */
export const SETTINGS_PAGES: NavEntry[] = [
  { to: '/settings/people', label: 'team', icon: 'users', keywords: ['people'] },
  { to: '/settings/inbox', label: 'inboxSettings', icon: 'inbox', keywords: ['inbox'] },
  { to: '/settings/tags', label: 'tags', icon: 'tag' },
  { to: '/settings/fields', label: 'fields', icon: 'field' },
  /** "Mon offre" (D-183): found by the plan, the price and the invoices too. */
  { to: '/settings/usage', label: 'usage', icon: 'gauge', keywords: ['offre', 'plan', 'facture'] },
  { to: '/settings/developers', label: 'developers', icon: 'braces', keywords: ['api', 'token', 'mcp', 'claude'], feature: 'apiAccess' },
];

export const USAGE_PATH = '/settings/usage';

/** Phone tab bar: the four core areas, then More. Automations, never "Flows" (Q5, A00-03). */
export const MOBILE_TABS: NavEntry[] = [
  { ...DASHBOARD, label: 'home' },
  INBOX,
  AUTOMATIONS,
  CONTACTS,
];

/** The More sheet: everything the tab bar leaves out. */
export const MORE_ITEMS: NavEntry[] = [ANALYTICS, POSTS, STORIES, CAMPAIGNS, CHANNELS, SETTINGS];

/** The palette's first view: the sidebar's pages, in its order (R40). */
export const MAIN_PAGES: NavEntry[] = [...NAV_GROUPS.flatMap((g) => g.items), ...NAV_FOOTER];

/** Every destination the ⌘K palette finds once something is typed: Templates and the settings pages too. */
export const ALL_PAGES: NavEntry[] = [...MAIN_PAGES, TEMPLATES, ...SETTINGS_PAGES];

export function isNavActive(entry: NavEntry, pathname: string): boolean {
  if (entry.end) return pathname === entry.to;
  if (entry.exclude && pathname.startsWith(entry.exclude)) return false;
  if (entry.also && (pathname === entry.also || pathname.startsWith(`${entry.also}/`))) return true;
  return pathname === entry.to || pathname.startsWith(`${entry.to}/`);
}
